import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Checkbox,
  Modal,
  InputAdornment,
  TextField,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import SearchIcon from "@mui/icons-material/Search";
import IndeterminateCheckBoxOutlinedIcon from "@mui/icons-material/IndeterminateCheckBoxOutlined";
import sortUp from "../../../assets/employee/sort_up.svg";
import sortDown from "../../../assets/employee/sort_down.svg";
import {
  useGetInviteEligibleEmployeesQuery,
  useSyncInviteEmployeesMutation,
} from "../../../redux-toolkit/features/promotions/promotionsApiSlice";
import { useGetAllCompaniesQuery } from "../../../redux-toolkit/features/company/companyApiSlice";
import { useAlert } from "../../../hooks/useAlert";
import { CompanyFilterDropdown } from "./CompanyFilterDropdown";
import "./AddEmployeesToEventModal.css";

const modalStyle = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: 560,
  maxWidth: "calc(100vw - 32px)",
  maxHeight: "90vh",
  display: "flex",
  flexDirection: "column",
  bgcolor: "background.paper",
  borderRadius: "12px",
  boxShadow: 24,
  outline: "none",
  overflow: "hidden",
};

const companyIdOf = (company) =>
  company?._id != null ? String(company._id) : "";

const compareByName = (a, b) =>
  String(a || "").localeCompare(String(b || ""), undefined, {
    sensitivity: "base",
  });

const sortEmployees = (rows, direction = "asc") =>
  [...rows].sort((a, b) => {
    const result =
      compareByName(a.employee?.name, b.employee?.name) ||
      compareByName(a.company?.name, b.company?.name);
    return direction === "desc" ? -result : result;
  });

export const AddEmployeesToEventModal = ({
  open,
  onClose,
  inviteId,
  sendImmediately = false,
  onSuccess,
}) => {
  const { displayAlert } = useAlert();
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [companyIds, setCompanyIds] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);
  const [sortDir, setSortDir] = useState("asc");

  const { data: companiesData } = useGetAllCompaniesQuery("", {
    skip: !open,
  });
  const companiesRaw =
    companiesData?.data?.companies || companiesData?.data || [];
  const companies = useMemo(() => {
    const list = Array.isArray(companiesRaw) ? [...companiesRaw] : [];
    return list.sort((a, b) => compareByName(a.name, b.name));
  }, [companiesRaw]);

  const { data, isLoading, isFetching } = useGetInviteEligibleEmployeesQuery(
    {
      inviteId,
      search: debouncedSearch || undefined,
    },
    { skip: !open || !inviteId, refetchOnMountOrArgChange: true }
  );

  const [syncEmployees, { isLoading: isSaving }] =
    useSyncInviteEmployeesMutation();

  const invite = data?.data?.invite;
  const allEmployees = data?.data?.employees || [];
  const employees = useMemo(() => {
    const selectedKeys = companyIds.map(String);
    const allCompanyIds = companies.map(companyIdOf).filter(Boolean);
    const selectedAll =
      !selectedKeys.length ||
      (allCompanyIds.length > 0 &&
        allCompanyIds.every((id) => selectedKeys.includes(id)));
    const filtered = selectedAll
      ? allEmployees
      : allEmployees.filter((row) =>
          selectedKeys.includes(String(row.company?._id || ""))
        );
    return sortEmployees(filtered, sortDir);
  }, [allEmployees, companyIds, companies, sortDir]);

  useEffect(() => {
    if (!open) return;
    const timer = setTimeout(() => setDebouncedSearch(search.trim()), 300);
    return () => clearTimeout(timer);
  }, [search, open]);

  useEffect(() => {
    if (!open) {
      setSearch("");
      setDebouncedSearch("");
      setCompanyIds([]);
      setSelectedIds([]);
      setSortDir("asc");
    }
  }, [open]);

  const selectedCount = selectedIds.length;
  const allSelected =
    employees.length > 0 &&
    employees.every((row) => selectedIds.includes(row._id));
  const someSelected =
    employees.some((row) => selectedIds.includes(row._id)) && !allSelected;

  const toggleAll = (checked) => {
    setSelectedIds(checked ? employees.map((row) => row._id) : []);
  };

  const toggleOne = (id, checked) => {
    setSelectedIds((prev) =>
      checked ? [...prev, id] : prev.filter((x) => x !== id)
    );
  };

  const handleClose = () => {
    if (isSaving) return;
    onClose?.();
  };

  const handleConfirmSelection = async () => {
    if (!selectedIds.length) {
      handleClose();
      return;
    }

    try {
      const result = await syncEmployees({
        inviteId,
        promotionIds: selectedIds,
        mode: "add",
      }).unwrap();

      displayAlert({
        open: true,
        message: sendImmediately
          ? `${selectedIds.length} employee${
              selectedIds.length === 1 ? "" : "s"
            } added and invited`
          : `${selectedIds.length} employee${
              selectedIds.length === 1 ? "" : "s"
            } added to event`,
        type: "success",
        timeout: "2500",
      });
      onSuccess?.(result?.data);
      handleClose();
    } catch (err) {
      displayAlert({
        open: true,
        message:
          err?.data?.message || err?.message || "Unable to add employees",
        type: "error",
        timeout: "3000",
      });
    }
  };

  const bookName = invite?.book?.name || "this";
  const title = invite
    ? `${invite.name}${invite.eventId ? ` - ${invite.eventId}` : ""}`
    : "Add employees";

  return (
    <Modal
      open={open}
      onClose={isSaving ? undefined : handleClose}
      aria-labelledby="add-employees-event-title"
    >
      <Box sx={modalStyle} className="add-employees-modal">
        <div className="add-employees-modal__head">
          <div>
            <h2 id="add-employees-event-title">{title}</h2>
            <p className="add-employees-modal__subtitle">
              Add qualified employees from{" "}
              <span className="add-employees-modal__book">{bookName}</span> book
            </p>
          </div>
          <button
            type="button"
            className="add-employees-modal__close"
            onClick={handleClose}
            disabled={isSaving}
            aria-label="Close"
          >
            <CloseIcon sx={{ fontSize: 18 }} />
          </button>
        </div>

        <div className="add-employees-modal__separator" />

        {sendImmediately && (
          <div
            className="add-employees-modal__warning"
            role="status"
          >
            Invites will be sent immediately to the selected employees. This
            cannot be undone.
          </div>
        )}

        <div className="add-employees-modal__search-row">
          <TextField
            fullWidth
            size="small"
            placeholder="Search employees by name or department..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ fontSize: 18, color: "#98a2b3" }} />
                </InputAdornment>
              ),
            }}
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: "8px",
                background: "#fff",
              },
            }}
          />
          <CompanyFilterDropdown
            companies={companies}
            appliedIds={companyIds}
            onApply={setCompanyIds}
            disabled={isSaving}
          />
        </div>

        <div className="add-employees-modal__list-head">
          <div className="add-employees-modal__list-head-main">
            <Checkbox
              size="small"
              checked={allSelected}
              indeterminate={someSelected}
              indeterminateIcon={<IndeterminateCheckBoxOutlinedIcon />}
              onChange={(e) => toggleAll(e.target.checked)}
              disabled={!employees.length || isSaving}
              sx={{
                color: "var(--pblue)",
                "&.Mui-checked": { color: "var(--pblue)" },
                "&.MuiCheckbox-indeterminate": { color: "var(--pblue)" },
              }}
            />
            <span className="add-employees-modal__list-title">
              Employees ({employees.length})
            </span>
          </div>
          <button
            type="button"
            className="add-employees-modal__sort-btn"
            onClick={() =>
              setSortDir((prev) => (prev === "asc" ? "desc" : "asc"))
            }
            disabled={!employees.length || isSaving}
            aria-label={
              sortDir === "asc"
                ? "Sort employees Z to A"
                : "Sort employees A to Z"
            }
            title={sortDir === "asc" ? "Sort Z–A" : "Sort A–Z"}
          >
            <img
              src={sortUp}
              alt=""
              className={
                sortDir === "asc"
                  ? "add-employees-modal__sort-icon--active"
                  : undefined
              }
            />
            <img
              src={sortDown}
              alt=""
              className={
                sortDir === "desc"
                  ? "add-employees-modal__sort-icon--active"
                  : undefined
              }
            />
          </button>
        </div>

        <div className="add-employees-modal__list">
          {(isLoading || isFetching) && (
            <p className="add-employees-modal__empty">Loading employees…</p>
          )}
          {!isLoading && !isFetching && employees.length === 0 && (
            <p className="add-employees-modal__empty">
              No eligible employees available to add
            </p>
          )}
          {employees.map((row) => {
            const checked = selectedIds.includes(row._id);
            return (
              <label
                key={row._id}
                className={`add-employees-modal__row ${
                  checked ? "add-employees-modal__row--selected" : ""
                }`}
              >
                <Checkbox
                  size="small"
                  checked={checked}
                  onChange={(e) => toggleOne(row._id, e.target.checked)}
                  disabled={isSaving}
                  sx={{
                    color: "var(--grey400)",
                    "&.Mui-checked": { color: "var(--pblue)" },
                  }}
                />
                <div className="add-employees-modal__row-info">
                  <span className="add-employees-modal__name">
                    {row.employee?.name || "—"}
                  </span>
                  <span className="add-employees-modal__dept">
                    {row.company?.name || "—"}
                  </span>
                </div>
              </label>
            );
          })}
        </div>

        <div className="add-employees-modal__footer">
          <span className="add-employees-modal__count">
            <span className="add-employees-modal__count-dot" />
            {selectedCount} employee{selectedCount === 1 ? "" : "s"} selected
          </span>
          <div className="add-employees-modal__actions">
            <Button
              type="button"
              variant="outlined"
              className="add-employees-modal__btn-cancel"
              onClick={handleClose}
              disabled={isSaving}
            >
              Cancel
            </Button>
            <Button
              type="button"
              variant="contained"
              className="add-employees-modal__btn-confirm"
              disableElevation
              onClick={handleConfirmSelection}
              disabled={isSaving || !selectedIds.length}
            >
              {isSaving
                ? sendImmediately
                  ? "Sending…"
                  : "Saving…"
                : sendImmediately
                  ? "Add & Send Invite"
                  : "Confirm Selection"}
            </Button>
          </div>
        </div>
      </Box>
    </Modal>
  );
};
